<template>
  <div @click="handleWhiteBackgroundOnClick($event)" id="media-page" class="page white" refs="mediaPage">
    <el-container class="workspace content-aside">
      <el-main class="workspace cpl-main">
        <div>
          <!-- Orientation -->
          <div class="orientation-options">
            <div class="items">
              <template v-if="isOrientationFilterVisible">
                <el-tooltip :popper-class="'custom-tooltip-popper-filters'" :show-arrow="false" effect="light" class="item" :content="$t('label.horizontalView')" placement="bottom">
                  <div @click="handleOrientationIconClick(CONST.ORIENTATION_VALUE_HORIZONTAL)"
                    :class="['item', 'horizontal', {'is-active': !isMediaOrientationVertical()}]">
                  </div>
                </el-tooltip>

                <el-tooltip :popper-class="'custom-tooltip-popper-filters'" :show-arrow="false" effect="light" class="item" :content="$t('label.verticalView')" placement="bottom">
                  <div @click="handleOrientationIconClick(CONST.ORIENTATION_VALUE_VERTICAL)"
                    :class="['item', 'vertical', {'is-active': isMediaOrientationVertical()}]">
                  </div>
                </el-tooltip>
            </template>
            </div>
          </div>
        </div>

        <!-- Main Content -->
        <div :style="{ width: '100%' }">
        <div class="options">
          <el-row :gutter="20">
            <!-- Media Search -->
            <el-col :span="24" class="search-col">
              <div class="workspace search">
                <media-search-bar
                  ref="mediaSearchBar"
                  :isMediaAlertTypeProp="isMediaComponentAsAlert"
                  @media-search-bar-change="handleMediaSearchBarChange($event)">
                </media-search-bar>
              </div>
            </el-col>
          </el-row>
        </div>

        <tag-slider
          :accessorComponentProp="mediaComponentAs"
          :refreshTagSliderProp="refreshTagSlider"
          :tagsSelectionRequestProp="tagsSelectionRequest"
          @tag-slider-change="handleTagSliderChange($event)">
        </tag-slider>

        <pop-identifier-slider
          v-if="showPopIdsSliderFilter"
          :accessorComponentProp="mediaComponentAs"
          :refreshPopIdsSliderProp="refreshPopIdsSlider"
          @popId-slider-change="handlePopIdsSliderChange($event)">
        </pop-identifier-slider>

        <led-tag-slider
          v-if="(isMediaComponentAsLed || isMediaComponentAsVideowall) && isLedTagsReady"
          :key="'ledTagSlider'"
          class="led-tags"
          :tagsProp="tagsSummaryFromTagsSlider"
          :popIdsProp="popIdsSummaryFromTagsSlider"
          :ledTagsProp="ledTags"
          :isVideowall="isMediaComponentAsVideowall"
          :refilterLedTagsProp="refilterLedTags"
          @led-tag-slider-change="handleLedTagSliderChange($event)">
        </led-tag-slider>

        <!-- Media Cards -->
        <div v-if="!isFetchingMedia && mediaList.itemsToShow.length === 0" class="no-search-results">{{$t('label.noSearchResults')}}</div>
        <div class="workspace media-row hide-scrollbar clear-media-selection-if-clicked main-content-row" id="mediaItemsContainer">
          <div id="mediaItemsToShow" :class="['clear-media-selection-if-clicked', { 'led' : isMediaComponentAsLed || isMediaComponentAsVideowall }]">
            <draggable
              v-for="(mediaItem, index) in mediaList.itemsToShow"
              v-bind="sortDraggableOptions"
              class="el-col el-col-8 media-item"
              tag="div"
              item-key="resourceId"
              ghost-class="sortable-ghost"
              :id="mediaItem.resourceId"
              :key="index"
              :style="mediaItem._edit.cardStyle"
              :list="[mediaItem]"
              :group="{ name: 'tags-shared', pull: false, put: true }"
              @add="handleTagDraggableAdd($event, mediaItem)"
            >
              <template #item="{ element: item }">
                <div
                  :id="item.resourceId"
                  :class="{
                    'selected': item._edit.selected,
                    'vertical': isMediaOrientationVertical(),
                    'hover': item._edit.isHover,
                    'active': item._edit.isActive,
                    'disabled-media-item': item.disabled
                  }"
                  :span="(!isMediaComponentAsLed && !isMediaComponentAsVideowall) ? (isMediaOrientationVertical() ? 6 : 8) : null"
                >
                  <div class="content">
                    <div :class="['media-preview-fixed',
                      {'firefox': ($utils.getBrowser() === 'Firefox'),
                      'disabled-media-item': item.disabled,
                      'relative': true,
                      'checkboard-background': item.hasOwnProperty('preview') && item.preview.type === CONST.MEDIA_TYPE_VALUE_IMAGE,
                      'audio-card': item.type === CONST.MEDIA_TYPE_VALUE_AUDIO }]"
                      :style="item._edit.previewElStyle">

                      <template v-if="item.hasOwnProperty('preview') && item.preview !== null">
                        <!-- For Not IE Browsers (IMAGE)-->
                        <img v-if="item.preview.hasOwnProperty('type') && item.preview.type === CONST.MEDIA_TYPE_VALUE_IMAGE && $utils.getBrowser() !== 'IE'"
                          :src="$utils.filterMediaPreviewUrl(item.preview.url)"
                          class="object-fit_cover_center"/>
                        <!-- For IE Browsers (IMAGE)-->
                        <div v-if="item.preview.hasOwnProperty('type') && item.preview.type === CONST.MEDIA_TYPE_VALUE_IMAGE && $utils.getBrowser() === 'IE'"
                          class="object-fit_fix_on_ie"
                          :style="{'backgroundImage': `url(${item.preview.url})`}">
                        </div>

                        <!-- For Not IE Browsers (LOGO)-->
                        <img v-if="item.preview.hasOwnProperty('type') && item.preview.type === 'logo' && $utils.getBrowser() === 'IE'"
                          :src="$utils.filterMediaPreviewUrl(item.preview.url)"
                          class="object-fit_cover_center"/>
                        <!-- For IE Browsers (LOGO)-->
                        <div v-if="item.preview.hasOwnProperty('type') && item.preview.type === 'logo' && $utils.getBrowser() === 'IE'"
                          class="object-fit_fix_on_ie"
                          :style="{'backgroundImage': `url(${item.preview.url})`}">
                        </div>

                        <!-- Project Preview -->
                        <template v-if="item.preview.type === 'svg'">
                          <iframe :id="`mediaProjectFile_${item.resourceId}`"
                            :src="$utils.filterMediaPreviewUrl(item.preview.url, { queryParams: (!isMediaComponentAsLed && !isMediaComponentAsVideowall) ? { dir: mediaFilters.orientation } : {} })"
                            @load="handleSVGOnLoad({elId: `mediaProjectFile_${item.resourceId}`})"
                            :class="['object-fit_cover_center', 'template-target-on-refresh-token', 'media-project-file', `media-preview-${item._edit.uid}`]"
                            :style="item._edit.previewElStyle">
                          </iframe>
                        </template>
                      </template>

                      <template v-if="(item.hasOwnProperty('preview') && item.preview === null) || (item.type === undefined)">
                        <div class="no-preview">
                          <div class="desc">
                            <el-icon class="el-icon-warning"><WarningFilled /></el-icon>
                            <p>{{$t('label.previewUnavailable')}}</p>
                          </div>
                        </div>
                      </template>

                      <!-- Audio -->
                      <template v-if="item.type && item.type === 'audio' && !item.status">
                        <div class="audio-preview">
                          <div class="audio-details-background"></div>
                          <div class="audio-details">
                            <template v-if="item.metadata && (item.metadata.title || item.metadata.artist)">
                              <div v-if="item.metadata && item.metadata.title" class="title">{{item.metadata.title}}</div>
                              <div v-if="item.metadata && item.metadata.artist" class="artist">{{item.metadata.artist}}</div>
                            </template>
                            <template v-else>
                              <div class="title">{{item.name}}</div>
                            </template>
                          </div>
                        </div>
                      </template>
                      <template v-else-if="item.type && item.type === 'audio' && item.preview && item.preview.status">
                        <iframe :id="`mediaProjectFile_${item.resourceId}`"
                          :src="$utils.filterMediaPreviewUrl(item.preview.url, { queryParams: (!isMediaComponentAsLed && !isMediaComponentAsVideowall) ? { dir: mediaFilters.orientation } : {} })"
                          @load="handleSVGOnLoad({elId: `mediaProjectFile_${item.resourceId}`})"
                          :class="['object-fit_cover_center', 'template-target-on-refresh-token', 'media-project-file', 'audio-iframe', `media-preview-${item._edit.uid}`]">
                        </iframe>
                      </template>
                      <!-- mask component for VideoWall -->
                      <bezels-mask
                        v-if="item.bezelsMask && !item.newLED && !item.newVideowall"
                        :bezels="item.bezelsMask.bezels"
                        :resolutionWidth="item.bezelsMask.resolutionWidth"
                        :resolutionHeight="item.bezelsMask.resolutionHeight"
                        :cardWidth="item.bezelsMask.cardWidth"
                        :cardHeight="item.bezelsMask.cardHeight" />
                    </div>
                    <span class="absolute bottom-[10px] right-[10px] text-xs">
                      <duration-input
                        v-if="item.preview.hasOwnProperty('type') && item.type === 'video' && !item.status"
                        :model-value="$utils.formatDurationToMinutesAndSeconds({ duration: item.dur })" readonly></duration-input>
                    </span>
                  </div>

                  <div v-if="item.newLED || item.newVideowall" class="led-label top-0 left-0 right-0 absolute h-[calc(100%-37px)]">
                    <el-icon class="justify-self-center el-icon-plus absolute left-0 right-0 top-[calc(50%-19px)] text-[38px]! text-[#C7C7C7]!">
                      <Plus />
                    </el-icon>
                    <span class="absolute left-0 right-0 px-3 top-[calc(53%+30px)] text-[#C7C7C7] text-xs">{{$t('label.canvasSize', { size: item.size })}}</span>
                  </div>

                  <div @click="handleMediaCardOnClick($event, item, index)"
                    @mouseover="handleMediaCardMouseHoverAndMouseOut('mouseover', item)"
                    @mouseout="handleMediaCardMouseHoverAndMouseOut('mouseout', item)"
                    :class="['preview-content-cover', {'disabled-media-item': (item.disabled)}]">
                  </div>

                  <div :class="['name', {'disabled-media-item': item.disabled}]">
                    <!-- Name Txt -->
                    <span v-if="(!inlineEditName.isInputVisible && inlineEditName.mediaResourceId === item.resourceId) ||
                      (inlineEditName.mediaResourceId !== item.resourceId)"
                      @click="handleInlineEditNameClick($event, item, index)"
                      :class="['media-item-name', {'has-status': (item.new || item.updated) || (item._edit && item._edit.modifiedFile) }]">
                      <span v-if="item._edit.modifiedFile && !item.new" class="status">{{$t('label.new')}}</span>
                      {{item.name}}
                    </span>

                    <!-- Edit Name Input -->
                    <el-input
                      class="edit-media-item-name"
                      id="media-item-name-input"
                      v-if="inlineEditName.isInputVisible && inlineEditName.mediaResourceId === item.resourceId"
                      :model-value="item.name"
                      @update:model-value="newValue => item.name = newValue"
                      :ref="`${item.resourceId}-editName`"
                      size="mini"
                      @blur="handleInlineEditNameInputConfirm(item, index)"
                      @focus="$event.target.select()">
                    </el-input>
                  </div>

                  <!-- Image/Project Preview button -->
                  <template v-if="item.preview.hasOwnProperty('type') && ['img', 'elementi'].includes(item.type) && !item.status">
                    <div class="image-preview-icon" @click="handleImagePreviewIconClick(item.preview)">
                      <font-awesome-icon icon="fa-solid fa-expand" class="w-full h-full"/>
                    </div>
                  </template>

                  <!-- Video Preview actions -->
                  <template v-if="item.preview.hasOwnProperty('type') && item.type === 'video' && !item.status">
                    <div @click="handleVideoPlayIconClick(item.videopreviews)" class="video-play-icon">
                      <font-awesome-icon icon="fa-regular fa-circle-play" class="w-full h-full" />
                    </div>
                  </template>

                  <!-- Audio Preview actions -->
                  <div v-if="item.type && item.type === 'audio' && !item.status" class="audio-preview-actions">
                    <template v-if="item._edit.selected">
                      <el-button v-if="!playAudio.isPlaying || playAudio.isPlaying && playAudio.audio.resourceId !== item.resourceId" @click="handleAudioPlayIconClick(item, index, $event)"  class="cstm-icon-btn-large play_white_bacgkround"></el-button>
                      <el-button v-if="playAudio.isPlaying && playAudio.audio.resourceId === item.resourceId" @click="handleAudioPauseIconClick(item, index, $event)" class="cstm-icon-btn-large pause_white_bacgkround"></el-button>
                    </template>
                    <template v-else>
                      <el-button v-if="!playAudio.isPlaying || playAudio.audio.resourceId !== item.resourceId"
                        @click="handleAudioPlayIconClick(item, index, $event)" class="cstm-icon-btn-large audio_note_white_bacgkround">
                      </el-button>
                      <el-button v-if="playAudio.isPlaying && playAudio.audio.resourceId === item.resourceId"
                        @click="handleAudioPauseIconClick(item, index, $event)"
                      class="cstm-icon-btn-large pause_white_bacgkround"></el-button>
                    </template>
                  </div>

                  <div class="drag-and-drop-hover"></div>

                  <!-- Card Actions -->
                  <template v-if="showCardActions">
                    <!-- Media Card Delete icon -->
                    <template v-if="enableDeleteMedia">
                      <div :class="['media-item-action-icon', 'delete', {'all-icon-hover': allMediaItemActionIconHover && !item.disabled, 'delete-single-item': item.disabled }]"
                        @click="handleMediaCardRemoveIconClick(item)"
                        @mouseover="handleMediaCardRemoveIconMouseHoverAndMouseOut('mouseover', item)"
                        @mouseout="handleMediaCardRemoveIconMouseHoverAndMouseOut('mouseout', item)">
                        <div class="icon"></div>
                      </div>
                    </template>

                    <!-- Copy Project Media icon -->
                    <template v-if="enableCopyMedia && item.preview.hasOwnProperty('type') && ['svg', 'html'].indexOf(item.preview.type) >= 0 && mediaSelection.length === 1 && !item.status && item.type !== 'audio'">
                      <div class="media-item-action-icon copy" @click="handleCopyMediaIconClick(item)">
                        <div class="icon"></div>
                      </div>
                    </template>

                    <!-- Edit Project Media icon -->
                    <template v-if="enableEditProject && item.preview.hasOwnProperty('type') && ['svg', 'html'].indexOf(item.preview.type) >= 0 && mediaSelection.length === 1 && !item.status && item.type !== 'audio'">
                      <div :class="['media-item-action-icon', 'edit', {'right-align': (!enableDeleteMedia && !enableCopyMedia)}]" @click="handleEditProjectClick(item)">
                        <div class="icon"></div>
                      </div>
                    </template>
                  </template>
                </div>
              </template>
            </draggable>
          </div>

          <div v-if="!mediaList.nextFetch.moreItems" class="media-items-bottom-space clear-media-selection-if-clicked"></div>
        </div>
        </div>
      </el-main>

      <!-- Right Side -->
      <el-aside class="workspace cpl-aside">
        <div class="workspace content">
          <loader v-if="fetchingMediaSelectionDetails" :isRequesting="true" :textLabel="''"></loader>

          <!-- File Upload Button -->
          <div
            class="default-right-settings-wrapper"
            :class="{ 'pb-9': showToggleToMediaV2 }"
          >
            <div
              v-if="showToggleToMediaV2"
              class="relative flex flex-col items-end justify-center gap-3 p-4 rounded-md bg-white w-fit top-16"
            >
              <div class="flex items-start gap-2">
                <FwbBadge
                  type="green"
                  class="rounded-md text-xs"
                >
                  <span>{{ $t('label.new') }}</span>
                </FwbBadge>

                <div class="flex flex-col items-start">
                  <span class="text-sm text-start font-semibold text-gray-900">{{ $t('media.mediaViewLegacyToV2Title') }}</span>
                  <span class="text-sm text-start text-gray-500">{{ $t('media.mediaViewSwitcherDescription') }}</span>
                </div>
              </div>

              <FwbButton
                class="justify-center border-none focus:ring-transparent!"
                color="green"
                data-component-element-id="media-list-legacy-switcher-btn"
                @click="$emit('toggle-to-media-v2')"
              >
                <div class="flex items-center gap-2">
                  <span>{{ $t('buttons.tryItNow') }}</span>
                  <BaseIconSvg
                    class="w-4! h-4!"
                    name="flowbite:arrow-right-outline"
                  />
                </div>
              </FwbButton>
            </div>

            <el-tooltip v-if="isFileUploadButtonVisible"
              :popper-class="'custom-tooltip-popper-filters'"
              :show-arrow="false"
              effect="light"
              :content="$t('label.upload')" placement="bottom">
              <div @click="handleFileUploadIconClick"
                id="filesPageUploadIcon" :class="{
                  'file-upload-icon': true,
                  'top-[172px]': !isLEDFormVisible && !isVideowallFormVisible,
                  'top-[298px]': isLEDFormVisible,
                  'top-[362px]': isVideowallFormVisible,
                  'top-[389px]': isLEDFormVisible && isResolutionCalculatorVisible,
                  'top-[452px]': isVideowallFormVisible && isResolutionCalculatorVisible}"></div>
            </el-tooltip>
          </div>

          <!-- New LED Button -->
          <el-button v-if="isNewLEDButtonVisible"
            @click="handleNewLedConfigClick"
            class="cstm-btn-gradient width-125 new-led-config">{{$t('label.new')}}</el-button>

          <!-- New LED Form -->
          <led-resolution-menu v-if="isLEDFormVisible"
            @led-resolution-menu-change="handleLedResolutionMenuChange($event)">
          </led-resolution-menu>

          <!-- New Videowall Button -->
          <el-button v-if="isNewVideowallButtonVisible"
            @click="handleNewVideowallConfigClick"
            class="cstm-btn-gradient width-auto new-videowall-config">{{$t('label.newCanvas')}}</el-button>

          <!-- New Videowall Form -->
          <led-resolution-menu v-if="isVideowallFormVisible"
            resolutionType="videowall"
            @led-resolution-menu-change="handleLedResolutionMenuChange($event)">
          </led-resolution-menu>

          <!-- Media Settings -->
          <media-menu v-if="isMediaSettingsVisible"
            ref="mediaProperties"
            :accessorComponentProp="isMediaComponentAsLed ? 'ledPage' : 'mediaPage'"
            :isMediaAlertTypeProp="isMediaComponentAsAlert"
            :formTabStyleProp="true"
            :mediaSelectionProp="mediaSelection"
            :userSchedulesProp="(isMediaComponentAsLed || isMediaComponentAsVideowall) ? userSchedulesFilteredByResolution : userSchedules"
            :confirmForceToDeleteMediaProp="confirmForceToDeleteMedia"
            :mediaPropertiesToHideProp="['delete']"
            :hasNoTemplateProp="hideMediaMenuComponentTemplate"
            :triggerPropertyActionProp="mediaMenuTriggerPropertyAction"
            :tagsSummaryProp="tagsSummaryForSuggestions"
            :popIdsSummaryProp="popIdsSummaryFromTagsSlider"
            :selectedTagsFromSliderProp="mediaFilters.mediaTags"
            @media-menu-change="handleMediaMenuChange($event)">

            <!-- Add media menu items inside this slot -->
            <template #addMenuItemSlot></template>
          </media-menu>

          <el-button v-if="isSelectAllMediaButtonVisible"
            @click="handleSelectAllVisibleMediaCard"
            :class="{
              'cstm-btn-grey': true,
              'width-125': true,
              'select-all-media': true,
              'white-background': true,
              'top-[360px]': !isLEDFormVisible && !isVideowallFormVisible,
              'top-[389px]': isLEDFormVisible,
              'top-[453px]': isVideowallFormVisible,
              'top-[480px]': isLEDFormVisible && isResolutionCalculatorVisible,
              'top-[543px]': isVideowallFormVisible && isResolutionCalculatorVisible}">{{$t('label.selectAll')}}
          </el-button>
        </div>
      </el-aside>
    </el-container>

    <!-- Image Preview -->
    <image-preview-dialog :imagePreview="imagePreview" @close="imagePreview.dialog = false"></image-preview-dialog>

    <!-- Play Video Dialog -->
    <video-player-dialog :playVideo="playVideo" @close="playVideo.dialog = false" />

    <!-- Play Audio Dialog -->
    <audio-player v-if="playAudio.audio"
      :playlistProp="[playAudio.audio]"
      :updateAudioPlayerProp="updateAudioPlayer"
      :accessorComponentProp="'mediaFilesPage'"
      :elClassProp="{'media-files-card-audio-player': 1}"
      @audio-player-change="handleAudioPlayerChange($event)">
    </audio-player>
  </div>
</template>

<script src="./media.js"></script>
<style lang="scss" src="./media.scss"></style>
